import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography } from '@mui/material';
import type { StepIconProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Button, Stepper } from 'framework/ui/mui-builder';
import { SyberryTodayPlanningStep } from 'domain/people/syberryToday/types';
import type { SyberryTodaySelectPanelProps } from '../../types';
import SyberryTodayTaskGroupList from '../SyberryTodayTaskGroupList/SyberryTodayTaskGroupList';

const STEP_ICON_CHECK_CLASS = 'syberry-today-step-icon-check';

const STEPS = [
  {
    planningStep: SyberryTodayPlanningStep.SELECT_TASKS,
    id: 'select-tasks',
    label: 'Select tasks',
    buttonLabel: 'Confirm task selection',
  },
  {
    planningStep: SyberryTodayPlanningStep.ORGANIZE_TASKS,
    id: 'organize-tasks',
    label: 'Organize tasks',
    buttonLabel: 'Confirm check-in',
  },
];

const ACTIVE_STEP_INDEX_BY_STEP: Record<SyberryTodayPlanningStep, number> = {
  [SyberryTodayPlanningStep.SELECT_TASKS]: 0,
  [SyberryTodayPlanningStep.ORGANIZE_TASKS]: 1,
  [SyberryTodayPlanningStep.CONFIRMED]: STEPS.length,
};

const STEPPER_SX: SxProps<Theme> = {
  '& .MuiStepLabel-root': {
    alignItems: 'center',
  },
  '& .MuiStepLabel-label': {
    color: 'text.primary',
    fontSize: '14px',
  },
  '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
    color: 'text.primary',
  },
  '& .MuiStepLabel-label.Mui-disabled': {
    color: 'text.secondary',
  },
  '& .MuiStepLabel-iconContainer': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 0,
    mr: '8px',
    background: 'transparent',
  },
  '& .MuiStepLabel-iconContainer.Mui-disabled': {
    background: 'transparent',
  },
  '& .MuiStepContent-root': {
    ml: '17px',
    pl: '25px',
    borderLeftColor: 'border.default',
    fontSize: '14px',
  },
  '& .MuiStepConnector-line': {
    borderColor: 'border.default',
  },
  [`& .${STEP_ICON_CHECK_CLASS}`]: {
    color: 'common.white',
    fill: 'currentColor',
  },
  '& .stepper-next-button': {
    minHeight: '30px',
    px: '18px',
    borderRadius: '4px',
    backgroundColor: 'secondary.main',
    color: 'common.white',
    fontSize: '14px',
    ml: '-6px',

    '&:hover': {
      backgroundColor: 'secondary.main',
    },

    '&.Mui-disabled': {
      backgroundColor: 'background.default',
      color: 'text.secondary',
    },
  },
  '& .stepper-back-button': {
    fontSize: '14px',
  },
};

const SyberryTodayStepIcon = ({ active, completed, icon }: StepIconProps) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      flex: '0 0 24px',
      alignSelf: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'text.secondary',
      color: 'common.white',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1,
      ...((active || completed) && {
        backgroundColor: 'secondary.main',
      }),
    }}
  >
    {completed ? <CheckIcon className={STEP_ICON_CHECK_CLASS} sx={{ position: 'relative', top: '-2px' }} /> : icon}
  </Box>
);

const SyberryTodaySelectPanel = ({
  activeStep,
  groups,
  activeGroupId,
  canConfirmTaskSelection,
  canConfirmCheckIn,
  isSkipCheckInDisabled,
  onSelectGroup,
  onConfirmTaskSelection,
  onBackToTaskSelection,
  onConfirmCheckIn,
  onOpenSkipCheckInModal,
}: SyberryTodaySelectPanelProps) => {
  const stepStateByPlanningStep = {
    [SyberryTodayPlanningStep.SELECT_TASKS]: {
      handleNext: onConfirmTaskSelection,
      isButtonDisabled: !canConfirmTaskSelection,
      description: (
        <SyberryTodayTaskGroupList groups={groups} activeGroupId={activeGroupId} onSelectGroup={onSelectGroup} />
      ),
    },
    [SyberryTodayPlanningStep.ORGANIZE_TASKS]: {
      handleNext: onConfirmCheckIn,
      isButtonDisabled: !canConfirmCheckIn,
      description: undefined,
    },
    [SyberryTodayPlanningStep.CONFIRMED]: {
      handleNext: onConfirmCheckIn,
      isButtonDisabled: true,
      description: undefined,
    },
  };
  const activeStepIndex = ACTIVE_STEP_INDEX_BY_STEP[activeStep];
  const handleNext = stepStateByPlanningStep[activeStep].handleNext;

  const steps = STEPS.map(({ planningStep, ...step }) => ({
    ...step,
    description: stepStateByPlanningStep[planningStep].description,
    isButtonDisabled: stepStateByPlanningStep[planningStep].isButtonDisabled,
  }));

  return (
    <Box
      component="aside"
      sx={{
        padding: 3,
        backgroundColor: 'background.paper',
        borderRight: 1,
        borderRightColor: 'common.separator',

        '@media (max-width: 900px)': {
          borderRight: 0,
          borderBottom: 1,
          borderBottomColor: 'common.separator',
        },
      }}
    >
      <Stepper
        activeStep={activeStepIndex}
        steps={steps}
        handleNext={handleNext}
        handleBack={onBackToTaskSelection}
        customStepComponent={SyberryTodayStepIcon}
        sx={STEPPER_SX}
      />

      <Typography variant="body2" color="textSecondary" mt={3}>
        You can{' '}
        <Button
          type="button"
          variant="text"
          sx={{
            minWidth: 0,
            padding: 0,
            color: 'secondary.main',
            fontSize: 'inherit',
            fontWeight: 400,
            lineHeight: 'inherit',
            verticalAlign: 'baseline',

            '&:hover': {
              background: 'transparent',
              textDecoration: 'underline',
            },
          }}
          disableRipple
          disabled={isSkipCheckInDisabled}
          onClick={onOpenSkipCheckInModal}
        >
          Skip check-in
        </Button>{' '}
        if you don't work today or want to postpone planning to continue later
      </Typography>
    </Box>
  );
};

export default SyberryTodaySelectPanel;
